import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import ProductHero from '../components/product/ProductHero';
import ProductGallery from '../components/product/ProductGallery';
import ProductDetails from '../components/product/ProductDetails';
import ProductFeatures from '../components/product/ProductFeatures';
import RelatedProducts from '../components/product/RelatedProducts';
import ProductCTA from '../components/product/ProductCTA';
import { fetchProductBySlug, fetchProductsByCategory } from '../services/api';

const ProductPage = () => {
  const { productSlug } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadProductData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch product by slug
        const productData = await fetchProductBySlug(productSlug);
        
        if (!productData) {
          console.warn(`Product not found for slug: ${productSlug}`);
          setNotFound(true);
          setLoading(false);
          return;
        }
        
        // Normalize product data structure (Strapi v4/v5 compatibility)
        const normalizedProduct = {
          id: productData.id,
          Title: productData.attributes?.Title || productData.Title,
          slug: productData.attributes?.slug || productData.slug,
          Description: productData.attributes?.Description || productData.Description,
          Image: productData.attributes?.Image || productData.Image,
          BackgroundImage: productData.attributes?.BackgroundImage || productData.BackgroundImage,
          Features: productData.attributes?.Features || productData.Features,
          Specifications: productData.attributes?.Specifications || productData.Specifications,
          Applications: productData.attributes?.Applications || productData.Applications,
          featured: productData.attributes?.featured || productData.featured,
          product_category: productData.attributes?.product_category || productData.product_category,
          ...productData.attributes
        };
        
        // Debug: Log the product data structure
        console.log('Product Data Structure:', {
          Features: normalizedProduct.Features,
          FeaturesType: typeof normalizedProduct.Features,
          Specifications: normalizedProduct.Specifications,
          SpecificationsType: typeof normalizedProduct.Specifications
        });
        
        setProduct(normalizedProduct);
        
        // Fetch related products from the same category
        if (normalizedProduct.product_category) {
          const categoryId = normalizedProduct.product_category.id || normalizedProduct.product_category;
          try {
            const categoryProducts = await fetchProductsByCategory(categoryId);
            setRelatedProducts(categoryProducts);
          } catch (err) {
            console.error('Error loading related products:', err);
            // Don't fail the whole page if related products fail
          }
        }
        
      } catch (err) {
        console.error('Error loading product data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (productSlug) {
      loadProductData();
    }
  }, [productSlug]);

  // Redirect to products page if product not found
  if (notFound) {
    return <Navigate to="/products" replace />;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-catalina-blue mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading product...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Product</h2>
            <p className="text-red-500 mb-4">{error}</p>
            <a
              href="/products"
              className="inline-block bg-catalina-blue text-white font-semibold py-2 px-6 rounded-lg hover:bg-san-marino transition duration-300"
            >
              Back to Products
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Get category info for breadcrumb
  const category = product?.product_category;

  return (
    <div>
      <ProductHero product={product} category={category} />
      <ProductGallery product={product} />
      <ProductDetails product={product} />
      <ProductFeatures product={product} />
      <RelatedProducts products={relatedProducts} currentProductId={product?.id} />
      <ProductCTA productName={product?.Title} />
    </div>
  );
};

export default ProductPage;